import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import SEO, { SEO_CONFIGS } from '../../components/SEO';

const RefundPolicy = () => {
  return (
    <MarketingLayout lightTheme>
      <SEO {...SEO_CONFIGS.refunds} />
      <section className="pt-32 pb-20 bg-gradient-to-b from-slate-50 to-white min-h-screen">
        <div className="container mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>

            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-slate-900">Refund Policy</h1>
            <p className="text-slate-500 mb-8">Last updated: January 2026</p>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
              <p className="text-slate-600 mb-8 leading-relaxed">
                This Refund Policy governs the purchase and use of Nanokart Credits and subscriptions on Nanokart.ai, 
                operated by NanoKart India Private Limited ("Nanokart", "we", "us").
              </p>

              <div className="space-y-8">
                {/* Section 1 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">1. Credit Purchases</h2>
                  <p className="text-slate-600 mb-4">
                    Nanokart Credits are prepaid usage credits required to access the Virtual Try-On and Moments APIs.
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Credits are non-refundable once consumed</li>
                    <li>Credits are not legal tender, non-transferable, and have no cash value</li>
                    <li>Promotional or bonus credits are non-refundable</li>
                  </ul>
                </div>

                {/* Section 2 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">2. Subscription Plans</h2>
                  <p className="text-slate-600 mb-4">Nanokart offers the following plans:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4 mb-4">
                    <li><strong>Starter</strong> (Pay-as-you-go)</li>
                    <li><strong>Growth</strong> (Monthly subscription)</li>
                    <li><strong>Enterprise</strong> (Monthly subscription)</li>
                  </ul>
                  <p className="text-slate-600">
                    Subscription fees grant access to discounted per-API pricing and plan-specific features. 
                    <strong> Subscription fees are non-refundable.</strong>
                  </p>
                </div>

                {/* Section 3 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">3. Refund Eligibility</h2>
                  <p className="text-slate-600 mb-4">Refunds may be considered only in the following cases:</p>
                  
                  <div className="space-y-4 ml-4">
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-2">a. Billing Errors</h3>
                      <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                        <li>Duplicate charges</li>
                        <li>Incorrect amounts charged due to system error</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-2">b. Extended Service Unavailability</h3>
                      <p className="text-slate-600">
                        Complete API unavailability for more than 24 consecutive hours, caused solely by Nanokart 
                        infrastructure (excluding maintenance or force majeure events)
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-2">c. Account Closure (Limited Case)</h3>
                      <p className="text-slate-600 mb-2">Unused purchased credits within 30 days of purchase, provided:</p>
                      <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                        <li>Account is voluntarily closed</li>
                        <li>No policy violations occurred</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-2">d. Verified Technical Failures</h3>
                      <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                        <li>Repeated API execution failures resulting in unusable outputs</li>
                        <li>System-level errors exceeding reasonable thresholds</li>
                      </ul>
                    </div>
                  </div>
                  
                  <p className="text-slate-600 mt-4 italic">
                    Refunds are not granted for subjective dissatisfaction with AI-generated outputs.
                  </p>
                </div>

                {/* Section 4 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">4. Non-Refundable Scenarios</h2>
                  <p className="text-slate-600 mb-4">Refunds will not be issued for:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Successfully executed API calls</li>
                    <li>Change of mind or reduced business usage</li>
                    <li>Business performance outcomes (sales, conversions, returns)</li>
                    <li>Account suspension or termination due to policy violations</li>
                    <li>Credits older than 90 days</li>
                    <li>Free or promotional credits</li>
                  </ul>
                </div>

                {/* Section 5 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">5. Subscription Cancellation</h2>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Subscriptions may be cancelled anytime</li>
                    <li>Benefits continue until the end of the billing cycle</li>
                    <li>No prorated refunds for partial months</li>
                  </ul>
                </div>

                {/* Section 6 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">6. Credit Validity</h2>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Purchased credits do not expire while the account remains active</li>
                    <li>If an account is inactive for 12 consecutive months, Nanokart may expire unused credits 
                        after providing 30 days' prior notice</li>
                  </ul>
                </div>

                {/* Section 7 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">7. Chargebacks & Disputes</h2>
                  <p className="text-slate-600 mb-2">
                    Initiating a chargeback without first contacting Nanokart may result in:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                    <li>Temporary account suspension</li>
                    <li>Investigation and possible termination</li>
                  </ul>
                </div>

                {/* Section 8 */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">8. Refund Process</h2>
                  <p className="text-slate-600 mb-4">To request a refund:</p>
                  <ol className="list-decimal list-inside text-slate-600 space-y-2 ml-4 mb-4">
                    <li>Log in to Partner Dashboard</li>
                    <li>Create a Support Ticket → Billing / Refund</li>
                    <li>Provide transaction details and reason</li>
                  </ol>
                  <p className="text-slate-600">
                    Approved refunds are processed within <strong>5-10 business days</strong> to the original payment method.
                  </p>
                </div>

                {/* Section 9 - Contact */}
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">9. Contact</h2>
                  <div className="flex flex-col sm:flex-row gap-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Email</p>
                        <a href="mailto:billing@nanokart.ai" className="text-blue-600 font-medium hover:underline">
                          billing@nanokart.ai
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Support Hours</p>
                        <p className="text-slate-700 font-medium">Mon-Fri, 9 AM - 6 PM IST</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default RefundPolicy;
